import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronLeft, Check, X, Plus, Copy, ImageIcon, Trash2, ArrowUp, ArrowDown, Trash, Loader2 } from 'lucide-react';
import QuillEditorWithImageResize from '../QuillEditorWithImageResize';
import CodeEditorWithMonaco from './CodeEditorWithMonaco';
import TestCaseManager, { TestCase } from './TestCaseManager';
import ScoringPointsEditor, { StructuredScoringConfig } from './ScoringPointsEditor';
import KnowledgePointSelector from './KnowledgePointSelector';
import QuestionPreviewModal from './QuestionPreviewModal';
import ImageUploadModal from '../ImageUploadModal';
import PythonQuestionEditor from './PythonQuestionEditor';
import CppQuestionEditor from './CppQuestionEditor';
import ScratchQuestionEditor from './ScratchQuestionEditor';
import SubjectiveQuestionEditor from './SubjectiveQuestionEditor';
import { getSupabaseUrl } from '../../supabase/client';

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  image?: string;
  imageWidth?: number;
  images?: string[];
  imageWidths?: number[];
}

interface Question {
  id: string;
  type: string;
  subType?: string;
  title: string;
  name?: string;
  content?: string;
  options: Option[];
  score: number;
  titleImages?: string[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  optionImageWidths?: Record<string, number>;
  scratchSubtype?: string;
  referenceSb3Url?: string;
  explanation?: string;
  answerText?: string;
  scoringPoints?: string;
  gradingMode?: 'oj_ai' | 'manual' | 'ai';
  showAnswer?: boolean;
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  answerImages?: string[];
  answerImageWidths?: number[];
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  scratchAnswerItems?: any[];
  scratchConfig?: any;
  difficulty?: 'easy' | 'medium' | 'hard';
  knowledgePointIds?: string[];
  commonMistakes?: string;
  timeLimitMs?: number;
  memoryLimitMb?: number;
  testCases?: TestCase[];
  structuredScoring?: StructuredScoringConfig;
  [key: string]: any;
}

interface QuizStyleQuestionViewProps<T extends Question = Question> {
  question: T;
  currentIndex: number;
  totalCount: number;
  examQuestions: T[];
  paperId?: string;
  onPrevQuestion: () => void;
  onNextQuestion: () => void;
  onExamQuestionsChange: (questions: T[]) => void;
  onEditQuestion?: (questionId: string) => void;
  hasWidthChanges: boolean;
  onSaveWidthSettings: () => void;
  isSavingWidths: boolean;
  onHasWidthChangesChange?: (hasChanges: boolean) => void;
  onExitEdit?: () => void;
  onSaveQuestion?: (question: T) => Promise<void>;
  onDeleteQuestion?: (questionId: string) => void;
  onCancelEdit?: () => void;
  onInsertQuestion?: (questionId: string) => void;
  onCopyQuestion?: (questionId: string) => void;
  onSaveQuestionLayout?: (questionId: string, layoutData: {
    titleImageWidths?: number[];
    titleImageLayout?: string;
    imagePosition?: string;
    optionImageWidths?: Record<string, number>;
    optionLayout?: string;
  }) => Promise<void>;
  showNavigation?: boolean;
}

const typeNames: Record<string, string> = {
  single_choice: '单选题',
  multi_choice: '多选题',
  judge: '判断题',
  subjective: '主观题',
  python_coding: 'Python编程题',
  cpp_coding: 'C++编程题',
  scratch_coding: 'Scratch编程题',
};

const difficultyOptions = [
  { value: 'easy', label: '简单', color: 'bg-green-100 text-green-700' },
  { value: 'medium', label: '中等', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'hard', label: '困难', color: 'bg-red-100 text-red-700' },
];

const gradingModeOptions = [
  { value: 'oj_ai', label: 'OJ执行+AI辅助分析' },
  { value: 'manual', label: '人工判分' },
];

export default function QuizStyleQuestionView<T extends Question = Question>({
  question,
  currentIndex,
  totalCount,
  examQuestions,
  paperId,
  onPrevQuestion,
  onNextQuestion,
  onExamQuestionsChange,
  onEditQuestion,
  hasWidthChanges,
  onSaveWidthSettings,
  isSavingWidths,
  onHasWidthChangesChange,
  onExitEdit,
  onSaveQuestion,
  onDeleteQuestion,
  onCancelEdit,
  onInsertQuestion,
  onCopyQuestion,
  onSaveQuestionLayout,
  showNavigation = true,
}: QuizStyleQuestionViewProps<T>) {
  // 编辑模式下选项图片尺寸限制 ref
  const editOptionImagesRef = useRef<HTMLDivElement>(null);
  const [editOptionImgMaxSize, setEditOptionImgMaxSize] = useState<string>('none');

  useEffect(() => {
    const container = editOptionImagesRef.current;
    if (!container) return;

    const updateMaxSize = () => {
      const card = container.closest('.bg-white.rounded-xl');
      if (card) {
        const cardWidth = (card as HTMLElement).offsetWidth;
        if (cardWidth > 0) {
          setEditOptionImgMaxSize(`${cardWidth * 0.5}px`);
        }
      }
    };

    updateMaxSize();

    const resizeObserver = new ResizeObserver(() => {
      updateMaxSize();
    });

    const card = container.closest('.bg-white.rounded-xl');
    if (card) {
      resizeObserver.observe(card);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!question) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 flex items-center justify-center">
        <p className="text-gray-400 text-sm">加载中...</p>
      </div>
    );
  }

  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const [editDraft, setEditDraft] = useState<T>(() => {
    return JSON.parse(JSON.stringify(question));
  });
  const [showPreview, setShowPreview] = useState(false);

  // 编辑会话 ID：切换题目时递增，强制非受控组件重新挂载
  // 注意：取消修改时不再递增，避免页面移位
  const [editSessionId, setEditSessionId] = useState(0);

  // 保存状态：防止重复点击
  const [isSaving, setIsSaving] = useState(false);

  // 原始题目快照：用于 isDirty 比较和取消修改时恢复
  // 使用 state 而非 ref，确保保存后更新能触发 isDirty 重新计算
  const [originalQuestion, setOriginalQuestion] = useState<T>(() => JSON.parse(JSON.stringify(question)));

  const [showOptionImageModal, setShowOptionImageModal] = useState(false);
  const [optionImageUploadTarget, setOptionImageUploadTarget] = useState<{ optionId: string } | null>(null);
  const [uploadedOptionImages, setUploadedOptionImages] = useState<string[]>([]);

  // 分值下拉框状态：支持常见分值选择和自定义输入
  const [isCustomScore, setIsCustomScore] = useState(false);
  const [customScoreInput, setCustomScoreInput] = useState('');
  const commonScores = [1, 2, 3, 5, 10, 15, 20];

  // 当 question prop 变化时，重置草稿并递增会话 ID
  // 仅在 question.id 变化（切换题目）时重置，避免每次渲染都重置导致用户输入丢失
  const prevQuestionIdRef = useRef<string | null>(null);
  React.useEffect(() => {
    if (question && question.id) {
      if (prevQuestionIdRef.current !== question.id) {
        const clonedQuestion = JSON.parse(JSON.stringify(question));
        setEditDraft(clonedQuestion);
        setOriginalQuestion(JSON.parse(JSON.stringify(question)));
        setEditSessionId(id => id + 1);
        setIsCustomScore(false);
        setCustomScoreInput('');
        prevQuestionIdRef.current = question.id;
      }
    }
  }, [question]);


  const updateDraft = (updates: Partial<Question>) => {
    setEditDraft(prev => ({ ...prev, ...updates } as T));
  };

  // 分值下拉框处理
  const handleScoreChange = (value: string) => {
    if (value === 'custom') {
      setIsCustomScore(true);
      setCustomScoreInput(String(editDraft.score));
      return;
    }
    setIsCustomScore(false);
    const newScore = parseInt(value);
    if (newScore > 0) {
      updateDraft({ score: newScore });
    }
  };

  const handleCustomScoreConfirm = () => {
    const newScore = parseInt(customScoreInput);
    if (newScore > 0) {
      updateDraft({ score: newScore });
      setIsCustomScore(false);
    }
  };

  const handleCustomScoreKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCustomScoreConfirm();
    } else if (e.key === 'Escape') {
      setIsCustomScore(false);
    }
  };

  const handleSave = async () => {
    if (unmountedRef.current || isSaving) return;
    setIsSaving(true);
    try {
      if (onSaveQuestionLayout) {
        await onSaveQuestionLayout(editDraft.id, {
          titleImageWidths: editDraft.titleImageWidths,
          titleImageLayout: editDraft.titleImageLayout,
          imagePosition: editDraft.imagePosition,
          optionImageWidths: editDraft.optionImageWidths,
          optionLayout: editDraft.optionLayout,
        });
      }

      if (unmountedRef.current) return;

      await onSaveQuestion?.(editDraft);

      if (unmountedRef.current) return;

      const newExamQuestions = examQuestions.map(q =>
        q.id === editDraft.id ? editDraft : q
      );
      onExamQuestionsChange(newExamQuestions);

      // 保存成功后，更新原始快照为当前 editDraft，使 isDirty 变为 false
      // 使用 setState 触发重渲染，按钮自动恢复置灰态
      if (!unmountedRef.current) {
        setOriginalQuestion(JSON.parse(JSON.stringify(editDraft)));
        onExitEdit?.();
      }
    } catch (error) {
      if (unmountedRef.current) return;
      console.error('保存题目失败:', error);
      alert('保存失败，请重试');
    } finally {
      if (!unmountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  const handleCancel = () => {
    // 用原始快照恢复 editDraft，确保内容回滚到未修改前状态
    // QuillEditorWithImageResize 内部会监听 value 变化，通过 Quill API 直接更新内容
    // 无需递增 editSessionId 重挂载组件，避免页面闪烁
    const restoredQuestion = JSON.parse(JSON.stringify(originalQuestion));
    setEditDraft(restoredQuestion);
    onCancelEdit?.();
  };

  // 规范化 HTML 用于比较：去除空白、空段、属性顺序差异、style 中的 width%
  // 解决 QuillEditor 挂载时 onChange 回传规范化 HTML（注入 width%、清理空段）导致 isDirty 误判的问题
  // 关键：图片宽度变化由 titleImageWidths 字段单独比较，HTML 中的 style=width% 必须忽略
  const normalizeHtmlForCompare = (html: any): string => {
    if (!html || typeof html !== 'string') return '';
    try {
      // 使用 DOMParser 解析并规范化，比正则更稳定
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      // 移除所有 img 的 style 属性（width% 由 titleImageWidths 单独管理）
      doc.querySelectorAll('img').forEach(img => {
        img.removeAttribute('style');
        // 移除可能的 data-* 临时属性
        Array.from(img.attributes).forEach(attr => {
          if (attr.name.startsWith('data-')) img.removeAttribute(attr.name);
        });
      });
      // 移除空段
      doc.querySelectorAll('p').forEach(p => {
        if (!p.textContent?.trim() && p.children.length === 0) p.remove();
      });
      // 序列化为规范化 HTML
      return doc.body.innerHTML
        .replace(/\s+/g, ' ')
        .replace(/>\s+</g, '><')
        .replace(/<br\s*\/?>/gi, '')
        .trim();
    } catch {
      // 解析失败时回退到简单的字符串规范化
      return html
        .replace(/\s+/g, ' ')
        .replace(/>\s+</g, '><')
        .replace(/<p>\s*<\/p>/gi, '')
        .replace(/<br\s*\/?>/gi, '')
        .replace(/style="[^"]*"/gi, '')
        .trim();
    }
  };

  const isDirty = useMemo(() => {
    if (!editDraft) return false;
    if (!originalQuestion) return false;
    const keysToCompare = [
      'title', 'name', 'options', 'explanation',
      'referenceAnswer', 'scoringPoints', 'score',
      'titleImageWidths', 'titleImageLayout', 'imagePosition',
      'optionLayout', 'optionImageWidths',
      // 编程题特有字段
      'testCases', 'timeLimitMs', 'memoryLimitMb',
      'difficulty', 'knowledgePointIds', 'codeTemplate',
      'commonMistakes', 'structuredScoring',
      // 主观题特有字段
      'maxTextLength', 'minTextLength', 'allowTextInput', 'gradingMode',
      // Scratch/编程题特有字段
      'scratchAnswerItems', 'scratchSubtype', 'scratchConfig',
      'referenceSb3Url', 'referenceAnswerImages', 'referenceAnswerImageWidths',
      'scoringPointsImages', 'scoringPointsImageWidths',
      'answerImages', 'answerImageWidths',
      'allowFileUpload', 'maxFileSize', 'allowImageUpload', 'maxImageSize',
      'graphicsAnswerItems', 'graphicsDifficulty', 'graphicsCategory', 'graphicsContent',
    ];
    for (const key of keysToCompare) {
      if (JSON.stringify(editDraft[key as keyof Question]) !== JSON.stringify(originalQuestion[key as keyof Question])) {
        return true;
      }
    }
    // content 字段单独处理：使用规范化比较，避免 QuillEditor 规范化导致误判
    const currentContent = normalizeHtmlForCompare(editDraft.content);
    const originalContent = normalizeHtmlForCompare(originalQuestion.content);
    if (currentContent !== originalContent) {
      return true;
    }
    return false;
  }, [editDraft, originalQuestion]);

  const handleOptionLayoutChange = (layout: 'grid' | 'list') => {
    const updatedQuestion = { ...question, optionLayout: layout } as T;
    const newExamQuestions = examQuestions.map(q =>
      q.id === question.id ? updatedQuestion : q
    );
    onExamQuestionsChange(newExamQuestions);
    setEditDraft(updatedQuestion);
  };

  const handleQuickScaleChange = (scale: number) => {
    const newOptions = question.options.map(o => ({
      ...o,
      imageWidth: o.image ? scale : o.imageWidth,
      imageWidths: o.images ? o.images.map(() => scale) : o.imageWidths
    }));

    const newOptionImageWidths: Record<string, number> = {};
    newOptions.forEach(o => {
      const key = o.id || o.label || '';
      if (o.image || (o.images && o.images.length > 0)) {
        newOptionImageWidths[key] = o.imageWidth || (o.imageWidths && o.imageWidths.length > 0 ? o.imageWidths[0] : scale);
      }
    });

    const updatedQuestion = { ...question, options: newOptions, optionImageWidths: newOptionImageWidths } as T;
    const newExamQuestions = examQuestions.map(q =>
      q.id === question.id ? updatedQuestion : q
    );
    onExamQuestionsChange(newExamQuestions);
    setEditDraft(updatedQuestion);
  };

  const updateOption = (optionId: string, updates: Partial<Option>) => {
    const newOptions = editDraft.options.map(o =>
      o.id === optionId ? { ...o, ...updates } : o
    );
    updateDraft({ options: newOptions });
  };

  const setCorrectAnswer = (optionId: string) => {
    const newOptions = editDraft.options.map(o => ({
      ...o,
      isCorrect: o.id === optionId
    }));
    updateDraft({ options: newOptions });
  };

  const toggleCorrectAnswer = (optionId: string) => {
    const newOptions = editDraft.options.map(o =>
      o.id === optionId ? { ...o, isCorrect: !o.isCorrect } : o
    );
    updateDraft({ options: newOptions });
  };

  const addOption = () => {
    const newLabel = String.fromCharCode(65 + editDraft.options.length);
    updateDraft({
      options: [...editDraft.options, {
        id: newLabel,
        label: newLabel,
        text: '',
        isCorrect: false
      }]
    });
  };

  const removeOption = (optionId: string) => {
    const newOptions = editDraft.options
      .filter(o => o.id !== optionId)
      .map((o, idx) => ({
        ...o,
        id: String.fromCharCode(65 + idx),
        label: String.fromCharCode(65 + idx)
      }));
    updateDraft({ options: newOptions });
  };

  const moveOption = (index: number, direction: 'up' | 'down') => {
    const newOptions = [...editDraft.options];
    if (direction === 'up' && index > 0) {
      [newOptions[index], newOptions[index - 1]] = [newOptions[index - 1], newOptions[index]];
    } else if (direction === 'down' && index < newOptions.length - 1) {
      [newOptions[index], newOptions[index + 1]] = [newOptions[index + 1], newOptions[index]];
    }
    const relabeled = newOptions.map((o, idx) => ({
      ...o,
      id: String.fromCharCode(65 + idx),
      label: String.fromCharCode(65 + idx)
    }));
    updateDraft({ options: relabeled });
  };

  const handleRunTest = async (code: string, testCases: TestCase[]) => {
    if (unmountedRef.current) return;
    try {
      const supabaseUrl = getSupabaseUrl();
      const response = await fetch(`${supabaseUrl}/functions/v1/code-grading`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'validate_test_cases',
          code,
          testCases,
          timeLimitMs: editDraft.timeLimitMs || 1000,
          memoryLimitMb: editDraft.memoryLimitMb || 128,
        }),
      });

      if (unmountedRef.current) return;

      const result = await response.json();

      if (unmountedRef.current) return;

      if (!response.ok) {
        throw new Error(result.error || '执行失败');
      }

      const { summary, results } = result;
      let message = `测试完成!\n通过: ${summary.passed}/${summary.total}\n得分: ${summary.totalScore}/${summary.maxScore}`;

      if (results.length > 0) {
        message += '\n\n详细结果:';
        results.forEach((r: any) => {
          const status = r.passed ? '✓' : '✗';
          message += `\n${status} 用例${r.caseIndex + 1}: ${r.passed ? '通过' : '失败'}`;
          if (!r.passed && r.errorMessage) {
            message += ` - ${r.errorMessage}`;
          }
        });
      }

      if (!unmountedRef.current) {
        alert(message);
      }
    } catch (error) {
      if (unmountedRef.current) return;
      const message = error instanceof Error ? error.message : '未知错误';
      console.error('测试用例验证失败:', error);
      alert(`测试执行失败: ${message}`);
    }
  };

  const handleUploadOptionImages = (optionId: string) => {
    setOptionImageUploadTarget({ optionId });
    setUploadedOptionImages([]);
    setShowOptionImageModal(true);
  };

  const handleOptionImageUploadComplete = (urls: string[]) => {
    if (!optionImageUploadTarget) return;

    const { optionId } = optionImageUploadTarget;
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option) return;

    const existingImages = option.images || [];
    const existingWidths = option.imageWidths || [];
    const newImages = [...existingImages, ...urls];
    const newWidths = [...existingWidths, ...Array(urls.length).fill(100)];

    updateOption(optionId, { images: newImages, imageWidths: newWidths });
    setShowOptionImageModal(false);
    setOptionImageUploadTarget(null);
  };

  const removeOptionImage = (optionId: string, imageIndex: number) => {
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option || !option.images) return;

    const newImages = option.images.filter((_, idx) => idx !== imageIndex);
    const newWidths = (option.imageWidths || []).filter((_, idx) => idx !== imageIndex);

    updateOption(optionId, { images: newImages, imageWidths: newWidths });
  };

  const updateOptionImageWidth = (optionId: string, imageIndex: number, width: number) => {
    const option = editDraft.options.find(o => o.id === optionId);
    if (!option || !option.imageWidths) return;

    const newWidths = [...option.imageWidths];
    newWidths[imageIndex] = width;

    const newOptionImageWidths = { ...(editDraft.optionImageWidths || {}) };
    newOptionImageWidths[`${optionId}_${imageIndex}`] = width;
    newOptionImageWidths[optionId] = width;

    updateDraft({
      options: editDraft.options.map(o =>
        o.id === optionId ? { ...o, imageWidths: newWidths } : o
      ),
      optionImageWidths: newOptionImageWidths,
    });
  };

  // 渲染客观题编辑面板
  const renderObjectiveEditor = () => {
    const isJudge = editDraft.type === 'judge';
    const isMulti = editDraft.type === 'multi_choice';

    return (
      <div ref={(el) => {
        (editOptionImagesRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }} className="mt-4 space-y-3">
        {editDraft.options.map((option, optIndex) => (
          <div key={option.id} className="border border-gray-200 rounded-lg p-3 bg-white">
            <div className="flex items-center gap-2">
              <button
                onClick={() => isMulti ? toggleCorrectAnswer(option.id) : setCorrectAnswer(option.id)}
                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  option.isCorrect ? 'border-green-500 bg-green-500' : 'border-gray-300'
                }`}
              >
                {option.isCorrect && <Check className="w-4 h-4 text-white" />}
              </button>
              <span className="text-sm font-medium text-gray-600 w-6 shrink-0">{option.label}.</span>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, { text: e.target.value })}
                placeholder={isJudge ? "选项内容" : `选项${optIndex + 1}`}
                disabled={isJudge}
                className={`w-full min-w-[200px] max-w-[400px] px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none ${isJudge ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
              <button
                onClick={() => handleUploadOptionImages(option.id)}
                disabled={isJudge}
                className={`p-1.5 rounded ${isJudge ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'}`}
                title={isJudge ? "判断题不支持上传选项图片" : "为选项添加图片"}
              >
                <ImageIcon className="w-4 h-4" />
              </button>
              {optIndex > 0 && (
                <button onClick={() => moveOption(optIndex, 'up')} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded" title="上移">
                  <ArrowUp className="w-4 h-4" />
                </button>
              )}
              {optIndex < editDraft.options.length - 1 && (
                <button onClick={() => moveOption(optIndex, 'down')} className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded" title="下移">
                  <ArrowDown className="w-4 h-4" />
                </button>
              )}
              {!isJudge && editDraft.options.length > 2 && (
                <button onClick={() => removeOption(option.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded" title="删除选项">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {(option.images && option.images.length > 0) && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {option.images.map((imgUrl, imgIdx) => (
                  <div key={imgIdx} className="relative group">
                    <img
                      src={imgUrl.startsWith('/sb-api/') ? imgUrl : imgUrl.startsWith('/storage/') ? `${getSupabaseUrl()}${imgUrl}` : imgUrl}
                      style={{
                        width: `${option.imageWidths?.[imgIdx] || 100}%`,
                        height: 'auto',
                        maxWidth: editOptionImgMaxSize,
                        maxHeight: editOptionImgMaxSize,
                        objectFit: 'contain',
                      }}
                      className="rounded border border-gray-200"
                      alt={`选项${option.label}图片${imgIdx + 1}`}
                    />
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={option.imageWidths?.[imgIdx] || 100}
                        onChange={(e) => updateOptionImageWidth(option.id, imgIdx, parseInt(e.target.value))}
                        className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <span className="text-xs text-gray-500 w-10 text-right">
                        {option.imageWidths?.[imgIdx] || 100}%
                      </span>
                      <button
                        onClick={() => removeOptionImage(option.id, imgIdx)}
                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        title="删除此图片"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {!isJudge && (
          <button onClick={addOption} className="flex items-center gap-1 text-blue-500 text-sm hover:text-blue-600 mt-2">
            <Plus className="w-4 h-4" />添加选项
          </button>
        )}
        <div className="mt-4">
          <div className="text-sm font-medium text-gray-700 mb-2">答案解析（学生提交试卷后可见）</div>
          <textarea
            value={editDraft.explanation || ''}
            onChange={(e) => updateDraft({ explanation: e.target.value })}
            placeholder="请输入答案解析..."
            className="w-full h-24 px-3 py-2 border border-gray-200 rounded resize-none focus:border-blue-500 focus:outline-none bg-white text-sm"
          />
        </div>
      </div>
    );
  };

  // 渲染编程题编辑面板
  const renderCodingEditor = () => {
    const isPython = editDraft.type === 'python_coding';
    const isCpp = editDraft.type === 'cpp_coding';
    const language = isPython ? 'python' : isCpp ? 'cpp' : 'javascript';

    const structuredScoring = editDraft.structuredScoring || {
      points: [],
      commonMistakes: editDraft.commonMistakes || ''
    };

    return (
      <div className="mt-4 space-y-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">题目难度</div>
          <div className="flex gap-2">
            {difficultyOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => updateDraft({ difficulty: opt.value as any })}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                  editDraft.difficulty === opt.value
                    ? opt.color + ' ring-2 ring-offset-1 ring-current'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">知识点</div>
          <KnowledgePointSelector
            selectedIds={editDraft.knowledgePointIds || []}
            onChange={(ids) => updateDraft({ knowledgePointIds: ids })}
          />
        </div>

        <CodeEditorWithMonaco
          value={editDraft.codeTemplate || ''}
          onChange={(value) => updateDraft({ codeTemplate: value })}
          language={language}
          height="250px"
          label="代码模板"
        />

        <CodeEditorWithMonaco
          value={editDraft.answerText || ''}
          onChange={(value) => updateDraft({ answerText: value })}
          language={language}
          height="250px"
          label="参考答案"
        />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">时间限制</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editDraft.timeLimitMs || 1000}
                onChange={(e) => updateDraft({ timeLimitMs: parseInt(e.target.value) || 1000 })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">ms</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">内存限制</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={editDraft.memoryLimitMb || 128}
                onChange={(e) => updateDraft({ memoryLimitMb: parseInt(e.target.value) || 128 })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">MB</span>
            </div>
          </div>
        </div>

        <TestCaseManager
          testCases={editDraft.testCases || []}
          onChange={(testCases) => updateDraft({ testCases })}
          onRunTest={handleRunTest}
          referenceCode={editDraft.answerText}
        />

        <ScoringPointsEditor
          config={structuredScoring}
          onChange={(config) => {
            updateDraft({
              structuredScoring: config,
              commonMistakes: config.commonMistakes
            });
          }}
        />

        <div>
          <div className="text-sm font-medium text-gray-700 mb-2">评分方式</div>
          <select
            value={editDraft.gradingMode || 'oj_ai'}
            onChange={(e) => updateDraft({ gradingMode: e.target.value as any })}
            className="px-3 py-2 border border-gray-200 rounded text-sm focus:border-blue-500 focus:outline-none w-full"
          >
            {gradingModeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  const renderEditorPanel = () => {
    if (editDraft.type === 'python_coding') {
      return (
        <PythonQuestionEditor
          key={`python_${editDraft.id}_${editSessionId}`}
          question={editDraft}
          editSessionId={editSessionId}
          onUpdateQuestion={(id, data) => updateDraft(data)}
        />
      );
    }

    if (editDraft.type === 'cpp_coding') {
      return (
        <CppQuestionEditor
          key={`cpp_${editDraft.id}_${editSessionId}`}
          question={editDraft}
          editSessionId={editSessionId}
          onUpdateQuestion={(id, data) => updateDraft(data)}
        />
      );
    }

    if (editDraft.type === 'scratch_coding') {
      return (
        <ScratchQuestionEditor
          key={`scratch_${editDraft.id}_${editSessionId}`}
          question={editDraft}
          paperId={paperId}
          editSessionId={editSessionId}
          onUpdateQuestion={(id, data) => updateDraft(data)}
        />
      );
    }

    if (editDraft.type === 'subjective') {
      return (
        <SubjectiveQuestionEditor
          key={`subjective_${editDraft.id}_${editSessionId}`}
          question={editDraft}
          paperId={paperId}
          editSessionId={editSessionId}
          onUpdateQuestion={(id, data) => updateDraft(data)}
        />
      );
    }

    switch (editDraft.type) {
      case 'single_choice':
      case 'multi_choice':
      case 'judge':
        return renderObjectiveEditor();
      default:
        return null;
    }
  };

  if (!question) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">点击左侧题型添加题目</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Question Header */}
      <div className="sticky lg:top-24 z-30 bg-gradient-to-r from-slate-50 to-slate-100 p-3 sm:p-4 lg:p-6 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <span className="px-2 py-0.5 lg:px-3 lg:py-1 bg-blue-600 text-white text-xs lg:text-sm font-medium rounded-full">
              {typeNames[question?.type] || '单选题'}
            </span>
          </div>
            {showNavigation && (
              <div className="hidden sm:flex items-center gap-2">
                <button onClick={onPrevQuestion} disabled={currentIndex === 0} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all shadow-sm">
                  <ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4" />上一题
                </button>
                <button onClick={onNextQuestion} disabled={currentIndex === totalCount - 1} className="px-3 lg:px-4 py-1.5 lg:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-all shadow-sm">
                  下一题<ChevronLeft className="w-3 h-3 lg:w-4 lg:h-4 rotate-180" />
                </button>
                {/* 分值下拉框：支持常见分值选择和自定义输入 */}
                {isCustomScore ? (
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={customScoreInput}
                    onChange={(e) => setCustomScoreInput(e.target.value)}
                    onKeyDown={handleCustomScoreKeyDown}
                    onBlur={handleCustomScoreConfirm}
                    autoFocus
                    className="w-14 lg:w-18 px-1.5 py-1.5 lg:py-2 bg-white border border-blue-300 rounded-lg text-center text-xs lg:text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 shadow-sm"
                    title="输入自定义分值"
                  />
                ) : (
                  <select
                    value={commonScores.includes(editDraft.score) ? String(editDraft.score) : 'custom'}
                    onChange={(e) => handleScoreChange(e.target.value)}
                    className="px-1.5 py-1.5 lg:py-2 bg-orange-500 text-white rounded-lg text-xs lg:text-sm font-medium cursor-pointer hover:bg-orange-600 transition-all shadow-sm border-0 focus:outline-none focus:ring-2 focus:ring-orange-400 appearance-none text-center"
                    title="选择分值"
                  >
                    {commonScores.map(s => (
                      <option key={s} value={String(s)} className="bg-white text-gray-700">{s}分</option>
                    ))}
                    <option value="custom" className="bg-white text-gray-700">自定义</option>
                  </select>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5 lg:gap-2 ml-auto">
{onInsertQuestion && (
  <button
    onClick={() => onInsertQuestion(question.id)}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-green-500 hover:bg-green-600 active:bg-green-700 transition-colors flex items-center gap-1.5 shadow-sm border border-green-400"
    title="在此题后插入新题"
  >
    <Plus className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">插入新题</span>
  </button>
)}
{onCopyQuestion && (
  <button
    onClick={() => onCopyQuestion(question.id)}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-blue-500 hover:bg-blue-600 active:bg-blue-700 transition-colors flex items-center gap-1.5 shadow-sm border border-blue-400"
    title="复制本题"
  >
    <Copy className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">复制</span>
  </button>
)}
{onDeleteQuestion && (
  <button
    onClick={() => {
      if (confirm('确定要删除本题吗?此操作不可撤销。')) {
        onDeleteQuestion(question.id);
      }
    }}
    className="px-3 py-1.5 text-white text-xs font-medium rounded-lg bg-red-500 hover:bg-red-600 active:bg-red-700 transition-colors flex items-center gap-1.5 shadow-sm border border-red-400"
    title="删除本题"
  >
    <Trash className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">删除</span>
  </button>
)}
{onSaveQuestion && (
  <button
    onClick={handleSave}
    disabled={!isDirty || isSaving}
    className={`px-3 py-1.5 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 shadow-sm border ${
      isDirty && !isSaving
        ? 'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 border-emerald-400'
        : 'bg-gray-300 cursor-not-allowed border-gray-300'
    }`}
  >
    {isSaving ? (
      <>
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        <span className="hidden sm:inline">保存中...</span>
      </>
    ) : (
      <>
        <Check className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">保存修改</span>
      </>
    )}
  </button>
)}
{onCancelEdit && (
  <button
    onClick={handleCancel}
    disabled={!isDirty || isSaving}
    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5 shadow-sm border ${
      isDirty && !isSaving
        ? 'text-gray-700 bg-white hover:bg-gray-100 active:bg-gray-200 border-gray-300'
        : 'text-gray-400 bg-gray-100 cursor-not-allowed border-gray-200'
    }`}
    title="取消修改"
  >
    <X className="w-3.5 h-3.5" />
    <span className="hidden sm:inline">取消修改</span>
  </button>
)}
            </div>
          </div>
        </div>

      {/* Question Content - 编辑模式 */}
      <div className="p-3 sm:p-4 lg:p-6 border-b border-gray-100">
        <div className="space-y-4">
          {/* 题干图片统一缩放条：所有题型通用，仅当题干中包含图片时显示 */}
          {(() => {
            const contentHtml = editDraft.content || editDraft.title || '';
            const hasImages = /<img/i.test(contentHtml);
            const currentWidths = editDraft.titleImageWidths || [];
            const imageCount = currentWidths.length || (contentHtml.match(/<img/gi) || []).length;
            // 取所有图片宽度的平均值作为统一缩放条的当前值
            const avgWidth = currentWidths.length > 0
              ? Math.round(currentWidths.reduce((a, b) => a + b, 0) / currentWidths.length)
              : 100;
            if (!hasImages || imageCount === 0) return null;
            return (
              <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <span className="text-xs text-gray-500 flex-shrink-0">题干图片统一缩放</span>
                <span className="text-xs text-gray-400 flex-shrink-0">({imageCount}张)</span>
                <div className="flex flex-wrap gap-1">
                  {[20, 30, 40, 50, 60, 70, 80, 90, 100].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => {
                        const newWidths = Array(imageCount).fill(preset);
                        updateDraft({ titleImageWidths: newWidths });
                      }}
                      className={`px-1.5 py-0.5 text-xs font-medium rounded transition-colors ${
                        avgWidth === preset
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <input
                    type="range"
                    min="10"
                    max="100"
                    value={avgWidth}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const newWidths = Array(imageCount).fill(val);
                      updateDraft({ titleImageWidths: newWidths });
                    }}
                    className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${avgWidth - 10}%, #e5e7eb ${avgWidth - 10}%, #e5e7eb 100%)`
                    }}
                  />
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={avgWidth}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      if (!isNaN(val) && val >= 10 && val <= 100) {
                        const newWidths = Array(imageCount).fill(val);
                        updateDraft({ titleImageWidths: newWidths });
                      }
                    }}
                    className="w-10 h-5 text-xs text-center border border-gray-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  />
                  <span className="text-xs text-gray-500">%</span>
                </div>
              </div>
            );
          })()}

          {/* 所有题型统一使用带题号的容器 */}
          <div>
            <div className="text-sm sm:text-base lg:text-lg font-medium text-gray-900 leading-[1.3]">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 font-bold flex-shrink-0">{currentIndex + 1}.</span>
                <div className="flex-1 min-w-0">
                  {/* 客观题用 QuillEditor */}
                  {editDraft.type !== 'python_coding' && editDraft.type !== 'cpp_coding' &&
                   editDraft.type !== 'scratch_coding' && editDraft.type !== 'subjective' ? (
                    <QuillEditorWithImageResize
                      key={`content_${editDraft.id}_${editSessionId}`}
                      value={editDraft.content || editDraft.title || ''}
                      onChange={(content, widths) => {
                        const updates: Partial<Question> = { content };
                        if (widths) updates.titleImageWidths = widths;
                        updateDraft(updates);
                      }}
                      questionId={editDraft.id}
                      targetField="content"
                      imageWidths={editDraft.titleImageWidths || []}
                      applyRealtimeScaling={true}
                    />
                  ) : (
                    // 非客观题用各自的编辑器组件
                    renderEditorPanel()
                  )}
                </div>
              </div>
            </div>
            {/* 客观题：题干下方渲染选项和答案解析（renderObjectiveEditor） */}
            {editDraft.type !== 'python_coding' && editDraft.type !== 'cpp_coding' &&
             editDraft.type !== 'scratch_coding' && editDraft.type !== 'subjective' && (
              renderEditorPanel()
            )}
          </div>
        </div>
      </div>

      {/* 题目预览弹窗 */}
      <QuestionPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        question={editDraft || question}
      />

      {/* 选项图片上传弹窗 */}
      {showOptionImageModal && optionImageUploadTarget && (
        <ImageUploadModal
          target={{ questionId: '', optionId: optionImageUploadTarget.optionId }}
          onClose={() => {
            setShowOptionImageModal(false);
            setOptionImageUploadTarget(null);
          }}
          onUpload={(imageData) => handleOptionImageUploadComplete([imageData])}
          allowMultiple={true}
        />
      )}
    </div>
  );
}
